import { useId, useState, type PointerEvent } from 'react'

type Day = { date: string; gmv: number; refunds: number }
const money = (value: number) => (value / 100).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export default function RevenueTrend({ days }: { days: Day[] }) {
  const gradient = useId()
  const [activeDate, setActiveDate] = useState<string | null>(null)
  const max = Math.max(1, ...days.map(day => day.gmv))
  const x = (index: number) => days.length === 1 ? 400 : 40 + index * 720 / Math.max(days.length - 1, 1)
  const y = (value: number) => 184 - value / max * 146
  const points = days.map((day, index) => `${x(index)},${y(day.gmv)}`).join(' ')
  const activeIndex = days.findIndex(day => day.date === activeDate)
  const active = days[activeIndex]
  const activeX = active ? x(activeIndex) : 0
  const activeY = active ? y(active.gmv) : 0
  const tooltipX = activeX > 560 ? activeX - 208 : activeX + 16
  const tooltipY = Math.max(24, Math.min(108, activeY - 38))

  function inspect(event: PointerEvent<SVGSVGElement>) {
    const svg = event.currentTarget
    const matrix = svg.getScreenCTM()
    if (!matrix || !days.length) return
    // Account for SVG letterboxing and responsive scaling, not just element width.
    const point = new DOMPoint(event.clientX, event.clientY).matrixTransform(matrix.inverse())
    if (point.x < 40 || point.x > 760 || point.y < 20 || point.y > 190) {
      setActiveDate(null)
      return
    }
    const index = Math.max(0, Math.min(days.length - 1, Math.round((point.x - 40) / 720 * (days.length - 1))))
    setActiveDate(days[index].date)
  }

  return <div className="ra-line-chart">
    <svg viewBox="0 0 800 225" role="group" aria-label="每日 GMV 趋势图，移动鼠标或按 Tab 查看每日销售额"
      onPointerMove={inspect} onPointerDown={inspect} onPointerLeave={() => setActiveDate(null)}>
      <defs><linearGradient id={gradient} x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="var(--accent)" stopOpacity=".22"/><stop offset="100%" stopColor="var(--accent)" stopOpacity="0"/></linearGradient></defs>
      {[0, 1, 2, 3].map(index => <g key={index} aria-hidden="true">
        <line x1="40" x2="760" y1={38 + index * 146 / 3} y2={38 + index * 146 / 3} strokeDasharray="4 5"/>
        <text x="0" y={42 + index * 146 / 3} fontSize="10">{(max * (1 - index / 3) / 100).toLocaleString('zh-CN', { maximumFractionDigits: 0 })}</text>
      </g>)}
      <polygon points={`40,184 ${points} 760,184`} fill={`url(#${gradient})`} aria-hidden="true"/>
      <polyline points={points} fill="none" strokeWidth="2.5" strokeLinejoin="round" aria-hidden="true"/>
      {days.map((day, index) => <g key={day.date}>
        <circle tabIndex={0} role="img" aria-label={`${day.date}，销售额 ¥${money(day.gmv)}`}
          onFocus={() => setActiveDate(day.date)} onBlur={() => setActiveDate(null)}
          cx={x(index)} cy={y(day.gmv)} r="5" className="ra-point"/>
        {(index === 0 || index === days.length - 1 || index % Math.max(1, Math.ceil(days.length / 6)) === 0) &&
          <text x={x(index)} y="213" fontSize="10" textAnchor="middle" aria-hidden="true">{day.date.slice(5)}</text>}
      </g>)}
      {active && <g className="ra-trend-inspection" pointerEvents="none">
        <line className="ra-trend-guide" x1={activeX} x2={activeX} y1="24" y2="184" strokeDasharray="4 4"/>
        <circle className="ra-trend-halo" cx={activeX} cy={activeY} r="10"/>
        <circle className="ra-trend-active" cx={activeX} cy={activeY} r="5"/>
        <g role="tooltip" aria-label={`${active.date} 销售额 ¥${money(active.gmv)}`} transform={`translate(${tooltipX},${tooltipY})`}>
          <rect className="ra-trend-tooltip" width="192" height="80" rx="8"/>
          <text className="ra-trend-tooltip-date" x="14" y="23">{active.date}</text>
          <text className="ra-trend-tooltip-label" x="14" y="52">销售额</text>
          <text className="ra-trend-tooltip-value" x="178" y="54" textAnchor="end">¥{money(active.gmv)}</text>
        </g>
      </g>}
    </svg>
  </div>
}
